import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart, ArrowRight, Users, BookOpen, Brain, TrendingUp, BarChart3,
  Lightbulb, Handshake, Award,
  Star, Quote, ChevronLeft, ChevronRight, Play,
  GraduationCap, FlaskConical, Globe2, Building2, Zap
} from 'lucide-react';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useSettings } from '../context/SettingsContext';
import api from '../utils/api';

/* ── Animated counter ─────────────────────────────────────────────────────── */
const Counter = ({ end, suffix = '', prefix = '', label, icon: Icon }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.4 });
  return (
    <div ref={ref} className="text-center">
      <div className="flex items-center justify-center mb-1">
        <Icon size={18} className="text-gold-400 mr-2 opacity-70" />
        <span className="font-display text-3xl lg:text-4xl font-bold text-white">
          {prefix}{inView ? <CountUp end={end} duration={2.5} separator="," /> : 0}{suffix}
        </span>
      </div>
      <p className="text-sm text-white/50 font-medium tracking-wide">{label}</p>
    </div>
  );
};

/* ── Program card ─────────────────────────────────────────────────────────── */
const DEPT_META = {
  research:     { icon: FlaskConical,  grad: 'from-blue-700 to-blue-900',   tag: 'Research & Analytics' },
  capacity:     { icon: GraduationCap, grad: 'from-emerald-700 to-teal-900', tag: 'Capacity Development' },
  innovation:   { icon: Lightbulb,     grad: 'from-violet-700 to-purple-900', tag: 'Social Innovation' },
  grants:       { icon: Award,         grad: 'from-amber-600 to-orange-800', tag: 'Grants & Partnerships' },
  education:    { icon: BookOpen,      grad: 'from-royal-700 to-royal-900',  tag: 'Education' },
  health:       { icon: Heart,         grad: 'from-pink-700 to-rose-900',    tag: 'Health' },
  environment:  { icon: Globe2,        grad: 'from-green-700 to-teal-900',   tag: 'Environment' },
  livelihood:   { icon: TrendingUp,    grad: 'from-orange-700 to-amber-900', tag: 'Livelihood' },
  youth:        { icon: Zap,           grad: 'from-violet-700 to-purple-900', tag: 'Youth' },
  women:        { icon: Users,         grad: 'from-pink-600 to-rose-800',    tag: 'Women' },
};

const ProgramCard = ({ program, categoryName, delay }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const meta = DEPT_META[program.category] || DEPT_META.education;
  const Icon = meta.icon;
  return (
    <div ref={ref} style={{ animationDelay: `${delay}ms` }}
      className={`group bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-500 hover:-translate-y-1.5 border border-gray-100 ${inView ? 'animate-fade-up' : 'opacity-0'}`}>
      <div className={`relative h-44 bg-gradient-to-br ${meta.grad} overflow-hidden`}>
        {program.image
          ? <img src={program.image} alt={program.title} className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-700" />
          : <div className="flex items-center justify-center h-full"><Icon size={48} className="text-white/20" /></div>}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <span className="absolute top-3 left-3 px-2.5 py-1 bg-white/15 backdrop-blur-sm text-white text-[10px] font-bold rounded-full border border-white/20 uppercase tracking-wide">
          {program.category}
        </span>
        {program.beneficiaries && (
          <div className="absolute bottom-3 right-3 text-right">
            <div className="text-white font-bold text-lg leading-none">{program.beneficiaries?.toLocaleString()}+</div>
            <div className="text-white/60 text-[10px]">participants</div>
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-display font-semibold text-gray-900 mb-2 leading-snug group-hover:text-royal-800 transition-colors">{program.title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed line-clamp-2 mb-4">{program.shortDescription || program.description}</p>
        <Link to={`/programs/${program._id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-royal-800 hover:gap-2.5 transition-all duration-200">
          Learn more <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
};

/* ── Blog card ────────────────────────────────────────────────────────────── */
const BlogCard = ({ post, delay }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <Link to={`/blog/${post._id}`} ref={ref} style={{ animationDelay: `${delay}ms` }}
      className={`group bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 flex flex-col border border-gray-100 ${inView ? 'animate-fade-up' : 'opacity-0'}`}>
      <div className="h-44 bg-hero-gradient relative overflow-hidden">
        {post.image
          ? <img src={post.thumbImage || post.image} alt={post.title} className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-700" />
          : <div className="flex items-center justify-center h-full"><BookOpen size={36} className="text-white/15" /></div>}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <span className="absolute top-3 left-3 px-2.5 py-1 bg-gold-500 text-royal-950 text-[10px] font-bold rounded-full uppercase tracking-wide capitalize">{post.category || 'Research'}</span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="text-xs text-gray-400 mb-2">
          {post.publishedAt ? new Date(post.publishedAt).toLocaleDateString('en-NG', { day:'numeric', month:'short', year:'numeric' }) : 'Recent'}
          {post.authorName && <span className="ml-2">· {post.authorName}</span>}
        </div>
        <h3 className="font-display font-semibold text-gray-900 leading-snug mb-2 group-hover:text-royal-800 transition-colors line-clamp-2 flex-1">{post.title}</h3>
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-royal-800 mt-3 group-hover:gap-2.5 transition-all">
          Read more <ArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
};

/* ── Four departments ─────────────────────────────────────────────────────── */
const DEPARTMENTS = [
  {
    icon: FlaskConical,
    color: 'bg-blue-50 text-blue-700',
    title: 'Research, Analytics & Evaluation',
    desc: 'Applied AI, psychometrics, behavioural science, and socio-pedagogical research producing peer-reviewed publications and policy briefs for government and international bodies.',
    items: ['Explainable & Educational AI (XAI-E)', 'Psychometrics & Assessment Analytics', 'Socio-Pedagogical Dynamics', 'Publications & Policy Briefs'],
    to: '/programs',
  },
  {
    icon: GraduationCap,
    color: 'bg-emerald-50 text-emerald-700',
    title: 'Capacity Development & Training',
    desc: 'High-impact professional development for educators, researchers, and institutions across West Africa — from digital pedagogy to advanced data science.',
    items: ['AI & Digital Literacy for Educators', 'Applied Research Methods & Data Science', 'Educational Leadership & Counselling', 'Certifications & Executive Workshops'],
    to: '/programs',
  },
  {
    icon: Lightbulb,
    color: 'bg-violet-50 text-violet-700',
    title: 'Social Innovation & Entrepreneurship',
    desc: 'Community-anchored educational initiatives driving equity, access, and sustainable socio-economic development for underserved populations.',
    items: ['EdTech & Community Incubator', 'Inclusive Education & Equity Programs', 'Institutional Consulting & HR Optimization', 'Youth Vocational & Employment Programs'],
    to: '/programs',
  },
  {
    icon: Handshake,
    color: 'bg-amber-50 text-amber-700',
    title: 'Strategic Grants & Partnerships',
    desc: 'A transparent hub for sponsored research, grant administration, and funded project monitoring with international funders and policy bodies.',
    items: ['Active & Completed Projects', 'Grant Writing & Proposal Hub', 'Research Fellows & Affiliates', 'International Donor Relations'],
    to: '/grants',
  },
];

/* ── Values (P.U.R.P.O.S.E.) ─────────────────────────────────────────────── */
const VALUES = [
  { letter: 'P', label: 'Precision & Rigor', desc: 'Uncompromising methodological excellence in psychometrics, mixed-methods research, and data analytics.' },
  { letter: 'U', label: 'Unbiased Innovation', desc: 'Ethical, transparent, and context-aware AI and technology adoption in educational and social contexts.' },
  { letter: 'R', label: 'Responsiveness', desc: 'Grounding interventions and policy recommendations in local realities and socio-cultural dynamics.' },
  { letter: 'P', label: 'People-Centred', desc: 'Prioritizing individual well-being, equity, and holistic human growth in every initiative.' },
  { letter: 'O', label: 'Open Collaboration', desc: 'Building multi-sectoral partnerships with academic institutions, government bodies, and international funders.' },
  { letter: 'S', label: 'Sustainability', desc: 'Creating long-term social enterprise models that empower communities beyond temporary funding cycles.' },
  { letter: 'E', label: 'Evidence-Based', desc: 'Every decision, program, and recommendation is rooted in rigorous empirical research and data.' },
];

const FALLBACK_TESTIMONIALS = [
  { _id:'1', name:'Dr. Fatima Al-Hassan', role:'Director of Education', organization:'Ministry of Education, Kano', content:'SAAI\'s psychometric assessment framework transformed how we evaluate teacher performance across the state. Their research rigor and cultural sensitivity are unmatched.', rating:5 },
  { _id:'2', name:'Prof. Emmanuel Eze', role:'Head of Research', organization:'University of Ibadan', content:'Collaborating with SAAI on our mixed-methods educational study was exceptional. Their AI-augmented analytics pipeline produced insights we couldn\'t have achieved independently.', rating:5 },
  { _id:'3', name:'Ms. Amina Suleiman', role:'Country Director', organization:'UNICEF Nigeria', content:'SAAI delivered a comprehensive policy brief on learning outcomes in underserved Northern communities that directly shaped our 2024 intervention strategy.', rating:5 },
];

const PARTNERS = [
  'UNESCO', 'UNICEF Nigeria', 'World Bank', 'SSRC', 'ACU', 'Ministry of Education', 'University of Ibadan', 'USAID'
];

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════════════ */
export default function HomePage() {
  const { s } = useSettings();
  const [programs,     setPrograms]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [blogs,        setBlogs]        = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [tIdx,         setTIdx]         = useState(0);
  const [videoOpen,    setVideoOpen]    = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    api.get('/programs?limit=6').then(r => setPrograms(r.data.items || [])).catch(() => {});
    api.get('/program-categories').then(r => setCategories(r.data || [])).catch(() => {});
    api.get('/blog?limit=3').then(r => setBlogs(r.data.items || [])).catch(() => {});
    api.get('/testimonials?limit=6').then(r => setTestimonials(r.data.items || [])).catch(() => {});
  }, []);

  const categoryName = (slug) => categories.find(c => c.slug === slug)?.name || slug;

  const tList = testimonials.length ? testimonials : FALLBACK_TESTIMONIALS;

  useEffect(() => {
    intervalRef.current = setInterval(() => setTIdx(i => (i + 1) % tList.length), 6000);
    return () => clearInterval(intervalRef.current);
  }, [tList.length]);

  const prevT = () => { clearInterval(intervalRef.current); setTIdx(i => (i - 1 + tList.length) % tList.length); };
  const nextT = () => { clearInterval(intervalRef.current); setTIdx(i => (i + 1) % tList.length); };

  const [heroRef,    heroInView]    = useInView({ triggerOnce: true });
  const [statsRef,   statsInView]   = useInView({ triggerOnce: true, threshold: 0.3 });
  const [deptRef,    deptInView]    = useInView({ triggerOnce: true, threshold: 0.1 });
  const [missionRef, missionInView] = useInView({ triggerOnce: true, threshold: 0.15 });
  const [testRef,    testInView]    = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <div className="min-h-screen bg-gray-50 font-body">
      <Navbar />

      {/* ══ HERO ═══════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-hero-gradient">
        {/* Grid bg */}
        <div className="absolute inset-0 opacity-25 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIwMjA2MCIgc3Ryb2tlLXdpZHRoPSIwLjUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')]" />
        {/* Orbs */}
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-gold-500/8 blur-3xl animate-pulse-slow" />
        <div className="absolute top-1/4 -right-20 w-80 h-80 rounded-full bg-gold-400/10 blur-2xl animate-float" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-royal-600/20 blur-3xl" />
        {/* Floating dots */}
        {[
          'top-1/3 left-[8%] w-2 h-2 bg-gold-400/60',
          'top-1/2 right-[12%] w-3 h-3 bg-gold-300/40',
          'top-[20%] left-[30%] w-1.5 h-1.5 bg-white/30',
          'bottom-1/3 right-[25%] w-2 h-2 bg-gold-500/40',
        ].map((cls, i) => (
          <div key={i} className={`absolute rounded-full animate-float ${cls}`} style={{ animationDelay: `${i * 1.5}s` }} />
        ))}

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left — copy */}
            <div ref={heroRef}>
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold-400/30 bg-gold-500/10 text-gold-300 text-xs font-bold tracking-widest uppercase mb-8 ${heroInView ? 'animate-fade-up' : 'opacity-0'}`}>
                <Star size={11} className="fill-gold-400 text-gold-400" />
                {s('hero_badge', 'Research · AI · Psychometrics · Capacity Development')}
                <Star size={11} className="fill-gold-400 text-gold-400" />
              </div>

              <h1 className={`font-display text-4xl sm:text-5xl lg:text-[3.4rem] font-bold leading-[1.1] tracking-tight mb-6 ${heroInView ? 'animate-fade-up animate-delay-100' : 'opacity-0'}`}>
                <span className="text-white">{s('hero_title', 'Pioneering Data-Driven Research & Educational Capacity Development')}</span>
              </h1>

              <p className={`text-white/60 text-lg leading-relaxed max-w-xl mb-8 ${heroInView ? 'animate-fade-up animate-delay-200' : 'opacity-0'}`}>
                {s('hero_subtitle', 'SAAI Institute bridges empirical research, artificial intelligence, and behavioural sciences — delivering rigorous educational evaluations and high-impact capacity development across West Africa.')}
              </p>

              {/* Tagline options */}
              <div className={`glass rounded-2xl px-5 py-3.5 border border-white/10 inline-block mb-8 ${heroInView ? 'animate-fade-up animate-delay-200' : 'opacity-0'}`}>
                <p className="text-gold-300 font-semibold text-sm italic">
                  "Bridging AI, Psychometrics, and Capacity Development across Africa."
                </p>
              </div>

              <div className={`flex flex-wrap gap-4 mb-10 ${heroInView ? 'animate-fade-up animate-delay-300' : 'opacity-0'}`}>
                <Link to="/programs" className="btn-primary text-base px-8 py-4 shadow-glow">
                  Explore Our Work <ArrowRight size={18} />
                </Link>
                <Link to="/partner" className="btn-outline-white text-base px-8 py-4">
                  Partner With Us <Handshake size={18} />
                </Link>
              </div>

              {/* Trust row */}
              <div className={`flex items-center gap-6 flex-wrap ${heroInView ? 'animate-fade-up animate-delay-400' : 'opacity-0'}`}>
                <div className="flex -space-x-2">
                  {['U','W','S','A','M'].map((l, i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-gold-400 to-amber-600 border-2 border-royal-900 flex items-center justify-center text-[10px] font-bold text-royal-950">
                      {l}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex gap-0.5 mb-0.5">{[...Array(5)].map((_, i) => <Star key={i} size={12} className="fill-gold-400 text-gold-400" />)}</div>
                  <p className="text-white/50 text-xs">Trusted by UNICEF, World Bank, UNESCO & more</p>
                </div>
              </div>
            </div>

            {/* Right — institute card */}
            <div className={`hidden lg:block relative ${heroInView ? 'animate-fade-in animate-delay-300' : 'opacity-0'}`}>
              <div className="glass rounded-3xl p-8 border border-white/15">
                <div className="absolute -top-5 -right-5 w-24 h-24 bg-gold-gradient rounded-2xl flex flex-col items-center justify-center shadow-gold rotate-6">
                  <Brain size={26} className="text-royal-950 mb-1" />
                  <span className="text-royal-950 font-bold text-[10px] text-center leading-tight">AI &<br/>Research</span>
                </div>

                <h3 className="font-display font-bold text-white text-lg mb-5">Institute Structure</h3>
                <div className="space-y-4">
                  {DEPARTMENTS.map((d, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${d.color}`}>
                        <d.icon size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-semibold text-xs truncate">{d.title.split('&')[0].trim()}</div>
                        <div className="text-white/40 text-[10px]">{d.items[0]}</div>
                      </div>
                      <div className="w-20 h-1.5 bg-white/10 rounded-full overflow-hidden flex-shrink-0">
                        <div className="h-full bg-gold-gradient rounded-full" style={{ width: ['90%','80%','70%','75%'][i] }} />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-3 pt-5 mt-5 border-t border-white/10">
                  {[{ v:'48+', l:'Research Projects' }, { v:'32+', l:'Policy Briefs' }, { v:'1.2K+', l:'Trained' }].map(s => (
                    <div key={s.l} className="text-center">
                      <div className="font-display font-bold text-lg text-gold-400">{s.v}</div>
                      <div className="text-white/40 text-[10px] leading-tight">{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating chips */}
              <div className="absolute -bottom-5 -left-8 glass rounded-2xl px-4 py-3 border border-white/15 animate-float" style={{ animationDelay: '1.5s' }}>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 bg-emerald-500/20 rounded-xl flex items-center justify-center"><BarChart3 size={15} className="text-emerald-400" /></div>
                  <div>
                    <div className="text-white font-bold text-xs">Africa</div>
                  {/* <div className="text-white/40 text-[10px]">& beyond West Africa</div> */}
                  </div>
                </div>
              </div>
              <div className="absolute top-4 -left-10 glass rounded-2xl px-4 py-2.5 border border-white/15 animate-float" style={{ animationDelay: '3s' }}>
                <div className="flex items-center gap-2">
                  <FlaskConical size={14} className="text-gold-400" />
                  <span className="text-white text-xs font-semibold">Ethical AI · XAI-E</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Diagonal clip */}
        <div className="absolute bottom-0 inset-x-0 h-24 bg-gray-50" style={{ clipPath: 'polygon(0 100%, 100% 100%, 100% 0)' }} />
      </section>

      {/* ══ IMPACT STATS ═══════════════════════════════════════════════════ */}
      <section ref={statsRef} className="bg-royal-900 py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`grid grid-cols-2 lg:grid-cols-4 gap-8 stagger ${statsInView ? 'animate-fade-in' : 'opacity-0'}`}>
            <Counter end={parseInt(s('stat_teachers','1200'))} suffix="+" label="Teachers Trained" icon={GraduationCap} />
            <Counter end={parseInt(s('stat_projects','48'))}  suffix="+"  label="Research Projects" icon={FlaskConical} />
            <Counter end={parseInt(s('stat_briefs','32'))}    suffix="+"  label="Policy Briefs" icon={BookOpen} />
            <Counter end={parseInt(s('stat_reach','15000'))}  suffix="+"  label="Community Reach" icon={Users} />
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
            <Link to="/impact" className="inline-flex items-center gap-2 px-6 py-2.5 bg-gold-500 hover:bg-gold-600 text-royal-950 font-semibold rounded-full text-sm transition-colors">
              <TrendingUp size={15} /> View Full Impact Dashboard
            </Link>
            <Link to="/insights" className="inline-flex items-center gap-2 px-6 py-2.5 border border-white/20 hover:border-white/40 text-white/70 hover:text-white font-semibold rounded-full text-sm transition-colors">
              <BarChart3 size={15} /> Data & Insights Portal
            </Link>
          </div>
        </div>
      </section>

      {/* ══ FOUR DEPARTMENTS ═══════════════════════════════════════════════ */}
      <section ref={deptRef} className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-14 ${deptInView ? 'animate-fade-up' : 'opacity-0'}`}>
            <span className="section-tag mb-4">Our Departments</span>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-gray-900 mb-4">What We Do</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-lg">Four integrated departments delivering research, training, innovation, and strategic partnerships across West Africa.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger">
            {DEPARTMENTS.map((d, i) => (
              <div key={i} className={`bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover border border-gray-100 transition-all duration-300 hover:-translate-y-1.5 flex flex-col ${deptInView ? 'animate-fade-up' : 'opacity-0'}`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${d.color}`}>
                  <d.icon size={22} />
                </div>
                <h3 className="font-display font-semibold text-gray-900 text-sm mb-3 leading-snug">{d.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed mb-4 flex-1">{d.desc}</p>
                <ul className="space-y-1.5 mb-5">
                  {d.items.map(item => (
                    <li key={item} className="flex items-start gap-1.5 text-xs text-gray-600">
                      <span className="text-gold-500 mt-0.5 flex-shrink-0">›</span>{item}
                    </li>
                  ))}
                </ul>
                <Link to={d.to} className="inline-flex items-center gap-1.5 text-xs font-semibold text-royal-800 hover:gap-2.5 transition-all mt-auto">
                  Learn more <ArrowRight size={12} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ MISSION / VISION ═══════════════════════════════════════════════ */}
      <section ref={missionRef} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className={missionInView ? 'animate-slide-right' : 'opacity-0'}>
              <span className="section-tag mb-5">Mission & Vision</span>
              <h2 className="font-display text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Africa's Premier <br />
                <span className="text-royal-800">Data-Driven</span><br />
                <span className="italic font-light text-gray-500">Research Institute.</span>
              </h2>
              <div className="space-y-5 mb-8">
                <div className="border-l-4 border-gold-500 pl-5 py-1">
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">Vision</div>
                  <p className="text-gray-700 leading-relaxed text-sm">{s('vision_statement', 'To be Africa\'s premier data-driven research institute, pioneering ethical AI, advanced psychometrics, and innovative educational frameworks for human flourishing and sustainable development.')}</p>
                </div>
                <div className="border-l-4 border-royal-800 pl-5 py-1">
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">Mission</div>
                  <p className="text-gray-700 leading-relaxed text-sm">{s('mission_statement', 'To bridge empirical research, artificial intelligence, and behavioural sciences by delivering rigorous educational evaluations, actionable policy analytics, and high-impact capacity development.')}</p>
                </div>
              </div>
              <div className="flex gap-4 flex-wrap">
                <Link to="/about" className="btn-royal">Our Story <ArrowRight size={16} /></Link>
                <button onClick={() => setVideoOpen(true)} className="inline-flex items-center gap-2 text-royal-800 font-semibold hover:gap-3 transition-all duration-200">
                  <div className="w-10 h-10 rounded-full border-2 border-royal-800 flex items-center justify-center hover:bg-royal-800 hover:text-white transition-colors">
                    <Play size={14} className="ml-0.5" />
                  </div>
                  Watch Our Story
                </button>
              </div>
            </div>

            {/* P.U.R.P.O.S.E. values */}
            <div className={`space-y-3 stagger ${missionInView ? 'animate-fade-up' : 'opacity-0'}`}>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Core Values — P.U.R.P.O.S.E.</div>
              {VALUES.map((v, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-royal-200 hover:bg-royal-50/30 transition-all duration-200">
                  <div className="w-9 h-9 bg-royal-800 rounded-xl flex items-center justify-center text-gold-400 font-display font-bold text-base flex-shrink-0">
                    {v.letter}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm mb-0.5">{v.label}</div>
                    <div className="text-gray-500 text-xs leading-relaxed">{v.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ PROGRAMS (dynamic) ═════════════════════════════════════════════ */}
      {programs.length > 0 && (
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <span className="section-tag mb-3">Active Programs</span>
                <h2 className="font-display text-4xl font-bold text-gray-900">Our Programs</h2>
              </div>
              <Link to="/programs" className="inline-flex items-center gap-1.5 text-royal-800 font-semibold hover:gap-3 transition-all text-sm">
                View all <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {programs.map((p, i) => <ProgramCard key={p._id} program={p} delay={i * 80} />)}
            </div>
          </div>
        </section>
      )}

      {/* ══ PARTNER / FUNDER CTA ═══════════════════════════════════════════ */}
      <section className="py-20 bg-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIwMjA2MCIgc3Ryb2tlLXdpZHRoPSIwLjUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] " />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="section-tag border-gold-400/30 text-gold-300 bg-gold-500/10 mb-5">Partner & Funder Portal</span>
              <h2 className="font-display text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                Commission Research.<br />Fund Impact.<br />
                <span className="text-gold-400 italic font-light">Build Together.</span>
              </h2>
              <p className="text-white/60 text-lg leading-relaxed mb-8">
                We invite international NGOs, Ministries of Education, academic institutions, and research grant bodies to initiate joint ventures or commission research with SAAI Institute.
              </p>
              <div className="flex gap-4 flex-wrap">
                <Link to="/partner" className="btn-primary text-base px-8 py-4">
                  <Handshake size={18} /> Initiate Partnership
                </Link>
                <Link to="/impact" className="btn-outline-white text-base px-8 py-4">
                  View Our Impact <ArrowRight size={16} />
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 stagger">
              {[
                { icon: '🏛️', title: 'Ministries of Education', desc: 'Policy analytics, teacher evaluation frameworks, and learning outcome research.' },
                { icon: '🌍', title: 'International NGOs', desc: 'Joint programs, co-funded research, and technical advisory services.' },
                { icon: '🎓', title: 'Academic Institutions', desc: 'Research partnerships, visiting fellows, and collaborative publications.' },
                { icon: '💰', title: 'Grant Bodies', desc: 'Competitive grant applications with UNESCO, World Bank, SSRC, and ACU.' },
              ].map(c => (
                <div key={c.title} className="glass rounded-2xl p-5 border border-white/15 hover:border-gold-400/40 transition-all duration-300 hover:-translate-y-1">
                  <div className="text-2xl mb-3">{c.icon}</div>
                  <div className="font-display font-bold text-sm text-white mb-1">{c.title}</div>
                  <div className="text-white/50 text-xs leading-relaxed">{c.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ BLOG ════════════════════════════════════════════════════════════ */}
      {blogs.length > 0 && (
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <span className="section-tag mb-3">Research & News</span>
                <h2 className="font-display text-4xl font-bold text-gray-900">Publications & Updates</h2>
              </div>
              <Link to="/blog" className="inline-flex items-center gap-1.5 text-royal-800 font-semibold hover:gap-3 transition-all text-sm">
                View all <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {blogs.map((b, i) => <BlogCard key={b._id} post={b} delay={i * 100} />)}
            </div>
          </div>
        </section>
      )}

      {/* ══ TESTIMONIALS ════════════════════════════════════════════════════ */}
      <section ref={testRef} className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-14 ${testInView ? 'animate-fade-up' : 'opacity-0'}`}>
            <span className="section-tag mb-4">What Partners Say</span>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-gray-900">Trusted by Leaders</h2>
          </div>
          <div className={`max-w-4xl mx-auto ${testInView ? 'animate-fade-up animate-delay-200' : 'opacity-0'}`}>
            <div className="relative bg-hero-gradient rounded-3xl p-10 lg:p-14 overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gold-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <Quote size={44} className="text-gold-400/20 mb-6" />
              <p className="text-white/85 text-xl lg:text-2xl font-display italic leading-relaxed mb-8 relative z-10">
                "{tList[tIdx]?.content}"
              </p>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gold-gradient flex items-center justify-center font-bold text-royal-950 text-lg overflow-hidden">
                    {tList[tIdx]?.avatar
                      ? <img src={tList[tIdx].avatar} alt={tList[tIdx].name} className="w-full h-full object-cover" />
                      : tList[tIdx]?.name?.[0]}
                  </div>
                  <div>
                    <div className="font-semibold text-white">{tList[tIdx]?.name}</div>
                    <div className="text-gold-400 text-sm">{tList[tIdx]?.role}{tList[tIdx]?.organization ? `, ${tList[tIdx].organization}` : ''}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-0.5">{[...Array(tList[tIdx]?.rating || 5)].map((_, i) => <Star key={i} size={14} className="fill-gold-400 text-gold-400" />)}</div>
                  <div className="flex gap-2">
                    <button onClick={prevT} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"><ChevronLeft size={16} className="text-white" /></button>
                    <button onClick={nextT} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"><ChevronRight size={16} className="text-white" /></button>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                {tList.map((_, i) => (
                  <button key={i} onClick={() => setTIdx(i)} className={`h-1.5 rounded-full transition-all duration-300 ${i === tIdx ? 'w-8 bg-gold-400' : 'w-1.5 bg-white/25'}`} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ PARTNERS STRIP ══════════════════════════════════════════════════ */}
      <section className="py-14 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-xs font-bold tracking-widest text-gray-400 uppercase mb-8">Collaborating with leading institutions</p>
          <div className="flex items-center justify-center gap-10 flex-wrap">
            {PARTNERS.map(p => (
              <div key={p} className="flex items-center gap-2 text-gray-300 hover:text-gray-500 transition-colors cursor-default">
                <Building2 size={14} />
                <span className="text-sm font-semibold">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ GET INVOLVED CTA ════════════════════════════════════════════════ */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <span className="section-tag mb-5">Join the Institute</span>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            How Will You<br /><span className="text-royal-800">Engage With SAAI</span>?
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto mb-12 leading-relaxed">
            Whether you commission research, fund a program, volunteer expertise, or partner institutionally — SAAI offers a structured pathway for engagement.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 text-left">
            {[
              { icon: FlaskConical, title:'Commission Research', desc:'Engage SAAI to conduct evaluations, policy analytics, or psychometric assessments for your institution.', cta:'Enquire Now', to:'/contact', border:'border-blue-200 hover:border-blue-400', btn:'bg-blue-700 text-white hover:bg-blue-800', bg:'bg-blue-50 text-blue-700' },
              { icon: Handshake,    title:'Strategic Partnership', desc:'Initiate joint ventures, co-funded research, or institutional collaborations with our team.', cta:'Partner With Us', to:'/partner', border:'border-royal-200 hover:border-royal-400', btn:'bg-royal-800 text-white hover:bg-royal-900', bg:'bg-royal-50 text-royal-700' },
              { icon: Heart,        title:'Support Our Mission', desc:'Fund capacity development programs, sponsor research fellowships, or donate to our community initiatives.', cta:'Support SAAI', to:'/donate', border:'border-gold-200 hover:border-gold-400', btn:'bg-gold-500 text-royal-950 hover:bg-gold-600', bg:'bg-gold-50 text-gold-700' },
            ].map(c => (
              <div key={c.title} className={`rounded-2xl p-7 border-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover bg-white ${c.border}`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${c.bg}`}>
                  <c.icon size={22} />
                </div>
                <h3 className="font-display font-bold text-xl text-gray-900 mb-2">{c.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">{c.desc}</p>
                <Link to={c.to} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:gap-3 ${c.btn}`}>
                  {c.cta} <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Video modal */}
      {videoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setVideoOpen(false)}>
          <div className="w-full max-w-3xl aspect-video bg-gray-900 rounded-2xl flex items-center justify-center" onClick={e => e.stopPropagation()}>
            <div className="text-center text-white/40">
              <Play size={48} className="mx-auto mb-3" />
              <p className="text-sm">Video coming soon</p>
              <button onClick={() => setVideoOpen(false)} className="mt-4 px-4 py-2 border border-white/20 rounded-full text-xs hover:bg-white/10 transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}